/**
 * The two ways a care rep brings a patient to the System Membership offer:
 *
 * - a rep's link, `/app/membership?invite=<token>`, sent after a call. It
 *   offers membership whatever the patient's A/B arm.
 * - a "View as patient" session: the admin login link lands on
 *   `/app?admin_session=<token>`, so a membership bought there is recorded as
 *   the rep's enrollment (sent to the API as `X-Admin-Session`).
 *
 * Both tokens are signed server-side and bound to one patient; the server
 * ignores them for anyone else or once expired. They are taken out of the URL
 * before analytics or the router see it, and kept in localStorage so they
 * survive the login redirect and onboarding detours.
 */

const INVITE_KEY = 'membership_invite';
const ADMIN_SESSION_KEY = 'membership_admin_session';

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the offer still works without the rep tag */
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Runs before analytics initialization and router creation. */
export function captureMembershipLinks() {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  let changed = false;
  const adminSession = params.get('admin_session');
  if (adminSession) {
    store(ADMIN_SESSION_KEY, adminSession);
    params.delete('admin_session');
    changed = true;
  }
  const invite = params.get('invite');
  if (invite && window.location.pathname.startsWith('/app/membership')) {
    store(INVITE_KEY, invite);
    params.delete('invite');
    changed = true;
  }
  if (!changed) return;
  // Keep the hash: a magic-link sign-in carries its session there.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${params.size ? `?${params}` : ''}${window.location.hash}`,
  );
}

export function membershipInvite(): string | null {
  return read(INVITE_KEY);
}

export function adminSessionHeaders(): Record<string, string> {
  const token = read(ADMIN_SESSION_KEY);
  return token ? { 'X-Admin-Session': token } : {};
}

/** After joining, the link has done its job. */
export function clearMembershipInvite() {
  try {
    localStorage.removeItem(INVITE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** On sign-out (a rep's session must not outlive it) and after a rep's
 *  enrollment: nothing later in this browser is the rep's. */
export function clearMembershipLinks() {
  clearMembershipInvite();
  try {
    localStorage.removeItem(ADMIN_SESSION_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Person properties for PostHog when a rep brought the patient to the offer
 *  (their link, or a View-as-patient session), so call-sold members can be
 *  filtered apart from patients who found it on Home. Empty for the A/B. */
export function repOfferTags(source: 'portal' | 'rep_link' | 'admin'): Record<string, unknown> {
  if (source === 'portal') return {};
  return {
    $set: { membership_rep_offer_source: source },
    $set_once: { membership_rep_offer_first_at: new Date().toISOString() },
  };
}
